import { Injectable, signal, effect } from '@angular/core';

export type Language = 'es' | 'en' | 'fr' | 'zh';

@Injectable({
    providedIn: 'root'
})
export class LanguageService {
    private readonly STORAGE_KEY = 'pinsapo_lang';

    // Current language signal
    readonly currentLang = signal<Language>(this.loadLang());

    constructor() {
        // Persist language choice
        effect(() => {
            localStorage.setItem(this.STORAGE_KEY, this.currentLang());
        });
    }

    setLanguage(lang: Language) {
        this.currentLang.set(lang);
    }

    private loadLang(): Language {
        const saved = localStorage.getItem(this.STORAGE_KEY) as Language;
        return (saved === 'es' || saved === 'en' || saved === 'fr' || saved === 'zh') ? saved : 'es';
    }

    // Dictionaries
    private readonly translations: Record<Language, any> = {
        es: {
            COMMON: {
                OPEN: 'Abierto',
                CLOSED: 'Cerrado',
                CANCEL: 'Cancelar',
                CATEGORIES: 'Categorías',
                SEARCH_PLACEHOLDER: 'Buscar productos o tiendas...',
                VIEW_ALL: 'Ver todo',
                BACK: 'Volver',
                CART: 'CARRITO',
                ORDERS: 'Pedidos',
                PROFILE: 'Perfil',
                HOME: 'Inicio',
                LOCATION: 'Localización',
                SELECT_CITY: 'Seleccionar Ciudad',
                ORDERING_FROM: 'Pidiendo a',
                NO_RESULTS: 'No hay resultados',
                ALL: 'Todos',
                LOADING: 'Cargando',
                FAVORITES: 'Favoritos',
                MENU: 'Menú',
                SLOGAN: 'Tu mercado local, directo a casa',
                NO_CATEGORIES: 'Sin categorías',
                SEARCH_ADVANCED: 'Buscador',
                FREE_DELIVERY: 'Envío gratis',
                VACATION_RENTAL: 'Alquiler Vacacional',
                NIGHT: 'noche',
                NIGHTS: 'noches',
                RESERVE_NOW: 'Reservar ahora',
                AVAILABLE: 'Disponible',
                UNAVAILABLE: 'No disponible',
                COMMENTS: 'Comentarios'
            },
            HOME: {
                LOCATION_PREFIX: 'Pedido en',
                LOCATION_SUFFIX: 'y alrededores',
                FEATURED_STORES: 'Tiendas destacadas',
                PROMO_TITLE: 'Compra de tu pueblo,',
                PROMO_SUBTITLE: '¡Envío a casa!',
                NO_STORES: 'No hay tiendas disponibles en',
                MAP_VIEW: 'Ver Mapa'
            },
            CART: {
                CLEAR_TITLE: '¿Vaciar cesta?',
                CLEAR_CONFIRM: '¿Estás seguro de que quieres eliminar todos los productos de tu cesta?',
                TITLE: 'Tu Pedido',
                EMPTY_TITLE: 'Tu carrito está vacío',
                EMPTY_DESC: '¡Busca lo que necesites en Pinsapo Market!',
                BROWSE: 'Ver Comercios',
                CLEAR: 'Limpiar',
                SUMMARY: 'Resumen del Pedido',
                SUBTOTAL: 'Subtotal',
                DELIVERY_FEE: 'Gastos de envío',
                TOTAL: 'Total',
                PAY_CARD: 'Pagar con Tarjeta',
                PAY_BIZUM: 'Pagar con Bizum',
                PAY_LOCAL: 'Pagar en el local',
                PAY_CASH: 'Pagar en efectivo',
                PAY_PHYSICAL_TPV: 'TPV físico',
                PHYSICAL_TPV_DESC: 'Paga con tarjeta cuando el repartidor te lleve tu pedido',
                PAYMENT_METHOD: 'Método de Pago',
                CARD_DESC: 'Pago seguro con tarjeta',
                BIZUM_DESC: 'Pago instantáneo con tu móvil',
                LOCAL_DESC: 'Paga cuando recojas tu pedido',
                DISCOUNT_CODE: 'Código de Descuento',
                APPLY: 'Aplicar',
                ENTER_CODE: 'Introduce tu código',
                DISCOUNT: 'Descuento',
                DISCOUNT_APPLIED: 'Descuento aplicado',
                DISCOUNT_INVALID: 'Código no válido o expirado',
                PROCESSING_ORDER: 'Procesando pedido...',
                PROCESSING_TPV: 'Conectando con el terminal TPV...',
                PROCESSING_BIZUM: 'Conectando con Bizum...',
                WAIT_TPV: 'Por favor, no cierre esta ventana',
                WAIT_BIZUM: 'Confirma el pago en tu aplicación de Bizum',
                SUCCESS_TITLE: '¡Pedido Confirmado!',
                SUCCESS_DESC: 'Tu pedido ha sido procesado correctamente.',
                TOTAL_PAID: 'Total pagado:',
                METHOD: 'Método:',
                BACK_HOME: 'Volver al Inicio',
                GO_TO_ORDERS: 'Ir a mis pedidos',
                DELIVERY_ADDRESS: 'Dirección de Entrega',
                SELECT_ADDRESS: 'Seleccionar Dirección',
                ADD_ADDRESS: 'Añadir nueva dirección',
                STREET: 'Calle y número',
                CITY: 'Ciudad',
                ZIP: 'Código Postal',
                LABEL: 'Etiqueta (Casa, Trabajo...)',
                SAVE_ADDRESS: 'Guardar Dirección',
                CHANGE_ADDRESS: 'Cambiar',
                UPDATE_ADDRESS: 'Actualizar Dirección',
                EDIT_ADDRESS: 'Editar dirección',
                CANCEL: 'Cancelar',
                CITY_RESTRICTION: 'No puedes añadir productos de distintas ciudades a la vez.',
                RESTAURANT_RESTRICTION: 'No puedes añadir productos de distintos restaurantes al mismo pedido.',
                DELIVERY_AVAILABLE: 'Envío a domicilio disponible',
                PICKUP_ONLY: 'Recogida en local'
            },
            RESTAURANT: {
                MENU: 'Carta',
                DELIVERY: 'Envío',
                ADDED: 'Añadido',
                RESERVE: 'Mesa reservada para',
                PICK_DATE: 'Fecha',
                DINERS: 'Comensales',
                TIME: 'Hora',
                CONTINUE_TO_MENU: 'Ver carta y pedir',
                TABLE_RESERVATION: 'Reserva de Mesa',
                RESERVATION_DATE: 'Fecha de Reserva',
                PLACE_ORDER: 'Hacer pedido',
                BOOK_STAY: 'Reservar Estancia',
                CHECK_IN: 'Entrada',
                CHECK_OUT: 'Salida',
                GUESTS: 'Huéspedes',
                SEE_AVAILABILITY: 'Ver Disponibilidad',
                VIEW_MENU_NO_DATES: 'Ver opciones sin fechas',
                VIEW_MENU_NO_RES: 'Ver la carta sin reservar',
                BOOK_APPOINTMENT: 'Reserva tu Cita',
                BOOK_TABLE: 'Reserva tu Mesa',
                APPOINTMENT_DATE: 'Día de la Cita',
                APPOINTMENT_TIME: 'Hora de la Cita',
                CHECK_AVAILABILITY: 'Ver disponibilidad',
                SEARCH_MENU: 'Buscar en la carta...',
                SEARCH_MENU_SHOP: 'Buscar producto...',
                SEARCH_MENU_RESERVATION: 'Buscar en los servicios...',
                RESERVE_ONLY: 'Reservar Mesa (Sin Pedido)',
                CHOOSE_OPTION: '¿Qué desea hacer?'
            },
            ORDERS: {
                TITLE: 'Mis Pedidos',
                ACTIVE: 'Pedido Activo',
                PAST: 'Pedidos Anteriores',
                STATUS_PENDING: 'Pendiente',
                STATUS_CONFIRMED: 'Confirmado',
                STATUS_PREPARING: 'Preparando',
                STATUS_DELIVERING: 'En camino',
                STATUS_DELIVERED: 'Entregado',
                STATUS_COMPLETED: 'Listo',
                STATUS_CANCELLED: 'Cancelado',
                STATUS_UNKNOWN: 'Estado desconocido',
                TRACK: 'Seguir Pedido',
                REORDER: 'Pedir de nuevo',
                TODAY: 'Hoy',
                YESTERDAY: 'Ayer',
                TOTAL: 'Total',
                NO_ORDERS: 'No hay pedidos'
            },
            PROFILE: {
                LOGOUT: 'Cerrar Sesión',
                EDIT: 'Editar',
                ORDERS: 'Mis Pedidos',
                ORDERS_SUBTITLE: 'Ver mi historial y estados',
                ADDRESSES: 'Mis Direcciones',
                ADDRESSES_SUBTITLE: 'Gestionar mis lugares de entrega',
                PAYMENTS: 'Métodos de Pago',
                LANGUAGE: 'Idioma',
                LANGUAGE_SUBTITLE: 'Cambiar el idioma de la aplicación',
                DEFAULT_BADGE: 'Predeterminada',
                FAVORITES: 'Mis Favoritos',
                FAVORITES_SUBTITLE: 'Tus restaurantes guardados',
                SET_AS_DEFAULT: 'Establecer como dirección predeterminada',
                DELETE_ACCOUNT: 'Eliminar Cuenta',
                DELETE_ACCOUNT_SUBTITLE: 'Desactivar tu cuenta permanentemente',
                DELETE_CONFIRM_TITLE: '¿Estás seguro?',
                DELETE_CONFIRM_BODY: 'Tu cuenta se bloqueará y no podrás volver a entrar. Para recuperarla deberás contactar con administración.',
                DELETE_MODAL_TITLE: 'Eliminar cuenta',
                DELETE_MODAL_DESC: 'Lamentamos mucho que te vayas. Por favor, dinos por qué quieres eliminar tu cuenta.',
                DELETE_REASON_LABEL: 'Motivo de la baja',
                DELETE_REASON_PLACEHOLDER: 'Selecciona un motivo',
                DELETE_COMMENTS_LABEL: 'Comentarios adicionales (opcional)',
                DELETE_COMMENTS_PLACEHOLDER: 'Cuéntanos un poco más...',
                DELETE_CONFIRM_CHECKBOX: 'Entiendo que esta acción es permanente y no podré recuperar mis datos.',
                DELETE_CONFIRM_BTN: 'Confirmar eliminación',
                REASON_EXPENSIVE: 'Es demasiado caro',
                REASON_SERVICE: 'Mal servicio',
                REASON_PRIVACY: 'Preocupaciones de privacidad',
                REASON_NOT_USEFUL: 'No me resulta útil',
                REASON_OTHER: 'Otro motivo'
            },
            PRODUCT: {
                DETAIL: 'Detalle del Producto',
                ADD_MORE: 'Añadir más',
                DESCRIPTION: 'Descripción',
                SOLD_OUT: 'Agotado',
                ONLY_LEFT: 'Solo quedan',
                STOCK_LIMIT: 'Límite de stock alcanzado'
            },
            AUTH: {
                LOGIN_TITLE: 'Iniciar Sesión',
                LOGIN_SUBTITLE: 'Bienvenido de nuevo a Pinsapo Market',
                SIGNUP_TITLE: 'Crear Cuenta',
                SIGNUP_SUBTITLE: 'Únete a Pinsapo Market',
                NAME_LABEL: 'Nombre Completo',
                NAME_PLACEHOLDER: 'Tu nombre',
                NAME_REQUIRED: 'El nombre es obligatorio',
                EMAIL_LABEL: 'Correo Electrónico',
                EMAIL_PLACEHOLDER: 'ejemplo@correo.com',
                EMAIL_REQUIRED: 'El correo es obligatorio',
                EMAIL_INVALID: 'Correo no válido',
                PASSWORD_LABEL: 'Contraseña',
                PASSWORD_PLACEHOLDER: 'Tu contraseña segura',
                PASSWORD_REQUIRED: 'La contraseña es obligatoria',
                PASSWORD_MINLENGTH: 'Mínimo 6 caracteres',
                CONFIRM_PASSWORD_LABEL: 'Repetir Contraseña',
                CONFIRM_PASSWORD_PLACEHOLDER: 'Repite tu contraseña',
                CONFIRM_PASSWORD_REQUIRED: 'Debes repetir la contraseña',
                PASSWORDS_NOT_MATCH: 'Las contraseñas no coinciden',
                PHONE_LABEL: 'Teléfono',
                PHONE_PLACEHOLDER: 'Tu número de teléfono',
                PHONE_REQUIRED: 'El teléfono es obligatorio',
                STREET_LABEL: 'Calle y número',
                STREET_PLACEHOLDER: 'C/ Ejemplo, 123',
                STREET_REQUIRED: 'La calle es obligatoria',
                CITY_LABEL: 'Ciudad',
                CITY_PLACEHOLDER: 'Tu ciudad',
                CITY_REQUIRED: 'La ciudad es obligatoria',
                ZIP_LABEL: 'Código Postal',
                ZIP_PLACEHOLDER: '12345',
                ZIP_REQUIRED: 'El código postal es obligatorio',
                BIRTHDATE_LABEL: 'Fecha de Nacimiento',
                BIRTHDATE_REQUIRED: 'La fecha de nacimiento es obligatoria',
                GENDER_LABEL: 'Sexo',
                GENDER_REQUIRED: 'El sexo es obligatorio',
                ACCEPTTERMS_REQUIRED: 'Debes aceptar las políticas para continuar.',
                ACCEPT_TERMS_REQUIRED: 'Debes aceptar las políticas para continuar.',
                SELECT_GENDER: 'Selecciona tu sexo',
                GENDER_MALE: 'Masculino',
                GENDER_FEMALE: 'Femenino',
                GENDER_OTHER: 'Otro',
                GENDER_PREFER_NOT: 'Prefiero no decirlo',
                POLICIES_TEXT: 'He leído y acepto las',
                POLICIES_LINK: 'políticas de Pinsapo Market',
                POLICIES_TITLE: 'Políticas de Pinsapo Market',
                POLICIES_UNDERSTOOD: 'Entendido',
                LOGIN_BUTTON: 'Entrar',
                SIGNUP_BUTTON: 'Registrarse',
                NO_ACCOUNT: '¿No tienes cuenta?',
                SIGNUP_LINK: 'Regístrate aquí',
                HAVE_ACCOUNT: '¿Ya tienes cuenta?',
                LOGIN_LINK: 'Inicia sesión aquí',
                CHECK_EMAIL: '¡Cuenta creada! Revisa tu email para confirmar.',
                EMAIL_NOT_CONFIRMED: 'Email no confirmado. Revisa tu bandeja de entrada.',
                INVALID_CREDENTIALS: 'Credenciales incorrectas.',
                FORGOT_PASSWORD_LINK: '¿Olvidaste tu contraseña?',
                FORGOT_PASSWORD_TITLE: 'Recuperar Contraseña',
                FORGOT_PASSWORD_SUBTITLE: 'Introduce tu email para recibir un enlace de recuperación',
                SEND_RESET_LINK: 'Enviar enlace',
                BACK_TO_LOGIN: 'Volver al inicio',
                RESET_PASSWORD_TITLE: 'Nueva Contraseña',
                RESET_PASSWORD_SUBTITLE: 'Introduce tu nueva contraseña',
                UPDATE_PASSWORD_BUTTON: 'Actualizar contraseña',
                PASSWORD_RESET_SENT: 'Email enviado. Revisa tu bandeja de entrada.',
                PASSWORD_UPDATED: 'Contraseña actualizada correctamente.',
                SESSION_EXPIRED: 'La sesión de recuperación ha expirado o no es válida. Por favor, solicita un nuevo enlace.',
                POLICIES_SECTION_1_TITLE: '1. Sobre Pinsapo Market',
                POLICIES_SECTION_1_DESC: 'Pinsapo Market es una plataforma tecnológica que sirve como puente entre los comercios locales (Partners) y sus clientes. Proporcionamos las herramientas digitales para que cada establecimiento gestione sus productos, reservas y pedidos de manera eficiente.',
                POLICIES_SECTION_2_TITLE: '2. Responsabilidad de los Establecimientos',
                POLICIES_SECTION_2_DESC: 'Cada Partner es el único responsable de la calidad de sus productos, la preparación de sus pedidos y el cumplimiento de sus reservas. Pinsapo Market no se hace responsable de posibles desperfectos, errores en los envíos o fallos en el servicio prestado por los establecimientos, los cuales actúan de forma independiente.',
                POLICIES_SECTION_3_TITLE: '3. Envíos y Pedidos',
                POLICIES_SECTION_3_DESC: 'La responsabilidad legal de cualquier incidencia logística (retrasos, daños durante el transporte o pedidos incorrectos) recae exclusivamente sobre el Partner propietario del servicio solicitado.',
                POLICIES_SECTION_4_TITLE: '4. Privacidad y GDPR',
                POLICIES_SECTION_4_DESC: 'Al crear una cuenta, aceptas que tratemos tus datos personales conforme a nuestra Política de Privacidad y el Reglamento General de Protección de Datos (GDPR). Tus datos solo se utilizarán para gestionar tus pedidos y ofrecerte una experiencia personalizada.',
                POLICIES_SECTION_5_TITLE: '5. Comunicaciones y Publicidad',
                POLICIES_SECTION_5_DESC: 'Aceptas que Pinsapo Market pueda enviarte comunicaciones comerciales, promociones y novedades sobre los establecimientos de tu zona. Podrás gestionar estas preferencias en cualquier momento desde tu perfil.'
            },
            FAVORITES: {
                EMPTY_TITLE: 'Aún no tienes favoritos',
                EMPTY_DESC: 'Guarda tus restaurantes preferidos para pedirlos más rápido.',
                BROWSE: 'Explorar Restaurantes',
                REMOVE: 'Eliminar de favoritos',
                RESTAURANTS: 'Restaurantes',
                PRODUCTS: 'Productos',
                GO_TO_RESTAURANT: 'Ir al restaurante'
            },
            BOOKING: {
                MAX_GUESTS: 'Máximo de huéspedes',
                PRIVATE_SPACE: 'Espacio único y privado',
                SECURE_BOOKING: 'Reserva 100% segura',
                ABOUT_PROPERTY: 'Sobre esta propiedad',
                CHOOSE_DATES: 'Elige tus fechas',
                DATES_UNAVAILABLE: 'Fechas no disponibles',
                NO_CHARGE_YET: 'No se te cobrará nada todavía',
                RULES_AND_SCHEDULE: 'Normas y Horarios',
                COMMENTS_PLACEHOLDER: 'Añadir peticiones especiales o comentarios adicionales...'
            }
        },
        en: {
            COMMON: {
                OPEN: 'Open',
                CANCEL: 'Cancel',
                CLOSED: 'Closed',
                CATEGORIES: 'Categories',
                SEARCH_PLACEHOLDER: 'Search products or stores...',
                VIEW_ALL: 'View all',
                BACK: 'Back',
                CART: 'CART',
                ORDERS: 'Orders',
                PROFILE: 'Profile',
                HOME: 'Home',
                LOCATION: 'Location',
                SELECT_CITY: 'Select City',
                ORDERING_FROM: 'Ordering from',
                NO_RESULTS: 'No results',
                ALL: 'All',
                FAVORITES: 'Favorites',
                MENU: 'Menu',
                LOADING: 'Loading',
                SLOGAN: 'Your local market, delivered to your door',
                NO_CATEGORIES: 'No categories',
                SEARCH_ADVANCED: 'Search',
                FREE_DELIVERY: 'Free delivery',
                VACATION_RENTAL: 'Vacation Rental',
                NIGHT: 'night',
                NIGHTS: 'nights',
                RESERVE_NOW: 'Reserve now',
                AVAILABLE: 'Available',
                UNAVAILABLE: 'Unavailable',
                COMMENTS: 'Comments'
            },
            HOME: {
                LOCATION_PREFIX: 'Order in',
                LOCATION_SUFFIX: 'and surrounding areas',
                FEATURED_STORES: 'Featured Stores',
                PROMO_TITLE: 'Shop from your town,',
                PROMO_SUBTITLE: 'Delivery to your door!',
                NO_STORES: 'No stores available in',
                MAP_VIEW: 'Map View'
            },
            CART: {
                CLEAR_TITLE: 'Clear cart?',
                CLEAR_CONFIRM: 'Are you sure you want to remove all items from your cart?',
                TITLE: 'Your Order',
                EMPTY_TITLE: 'Your cart is empty',
                EMPTY_DESC: 'Find whatever you need at Pinsapo Market!',
                BROWSE: 'Browse Stores',
                CLEAR: 'Clear',
                SUMMARY: 'Order Summary',
                SUBTOTAL: 'Subtotal',
                DELIVERY_FEE: 'Delivery Fee',
                TOTAL: 'Total',
                PAY_CARD: 'Pay with Card',
                PAY_BIZUM: 'Pay with Bizum',
                PAY_LOCAL: 'Pay at Local',
                PAY_CASH: 'Pay with Cash',
                PAY_PHYSICAL_TPV: 'Physical POS',
                PHYSICAL_TPV_DESC: 'Pay with card when the delivery person arrives',
                PAYMENT_METHOD: 'Payment Method',
                CARD_DESC: 'Secure card payment',
                BIZUM_DESC: 'Instant mobile payment',
                LOCAL_DESC: 'Pay when you pick up',
                DISCOUNT_CODE: 'Discount Code',
                APPLY: 'Apply',
                ENTER_CODE: 'Enter your code',
                DISCOUNT: 'Discount',
                DISCOUNT_APPLIED: 'Discount applied',
                DISCOUNT_INVALID: 'Invalid or expired code',
                PROCESSING_ORDER: 'Processing order...',
                PROCESSING_TPV: 'Connecting to TPV terminal...',
                PROCESSING_BIZUM: 'Connecting to Bizum...',
                WAIT_TPV: 'Please do not close this window',
                WAIT_BIZUM: 'Confirm the payment in your Bizum app',
                SUCCESS_TITLE: 'Order Confirmed!',
                SUCCESS_DESC: 'Your order has been processed successfully.',
                TOTAL_PAID: 'Total Paid',
                METHOD: 'Method',
                BACK_HOME: 'Back Home',
                GO_TO_ORDERS: 'Go to my orders',
                DELIVERY_ADDRESS: 'Delivery Address',
                SELECT_ADDRESS: 'Select Address',
                ADD_ADDRESS: 'Add new address',
                STREET: 'Street and number',
                CITY: 'City',
                ZIP: 'ZIP Code',
                LABEL: 'Label (Home, Work...)',
                SAVE_ADDRESS: 'Save Address',
                CHANGE_ADDRESS: 'Change',
                UPDATE_ADDRESS: 'Update Address',
                EDIT_ADDRESS: 'Edit address',
                CANCEL: 'Cancel',
                CITY_RESTRICTION: 'You cannot add products from different cities at the same time.',
                RESTAURANT_RESTRICTION: 'You cannot add products from different restaurants to the same order.',
                DELIVERY_AVAILABLE: 'Delivery available',
                PICKUP_ONLY: 'Pickup only'
            },
            RESTAURANT: {
                MENU: 'Menu',
                DELIVERY: 'Delivery',
                ADDED: 'Added',
                RESERVE: 'Table reserved for',
                PICK_DATE: 'Pick a date',
                DINERS: 'Diners',
                TIME: 'Time',
                CONTINUE_TO_MENU: 'See menu and order',
                TABLE_RESERVATION: 'Table Reservation',
                RESERVATION_DATE: 'Reservation Date',
                PLACE_ORDER: 'Place Order',
                BOOK_STAY: 'Book Stay',
                CHECK_IN: 'Check-in',
                CHECK_OUT: 'Check-out',
                GUESTS: 'Guests',
                SEE_AVAILABILITY: 'See Availability',
                VIEW_MENU_NO_DATES: 'View options without dates',
                VIEW_MENU_NO_RES: 'View menu without reservation',
                BOOK_APPOINTMENT: 'Book Appointment',
                BOOK_TABLE: 'Book Table',
                APPOINTMENT_DATE: 'Appointment Date',
                APPOINTMENT_TIME: 'Appointment Time',
                CHECK_AVAILABILITY: 'Check Availability',
                SEARCH_MENU: 'Search menu...',
                SEARCH_MENU_SHOP: 'Search product...',
                SEARCH_MENU_RESERVATION: 'Search services...',
                RESERVE_ONLY: 'Book Table Only (No Order)'
            },
            ORDERS: {
                TITLE: 'Your Orders',
                ACTIVE: 'Active Order',
                PAST: 'Past Orders',
                STATUS_PENDING: 'Pending',
                STATUS_CONFIRMED: 'Confirmed',
                STATUS_PREPARING: 'Preparing',
                STATUS_DELIVERING: 'On the way',
                STATUS_DELIVERED: 'Delivered',
                STATUS_COMPLETED: 'Ready',
                STATUS_CANCELLED: 'Cancelled',
                STATUS_UNKNOWN: 'Unknown status',
                TRACK: 'Track Order',
                REORDER: 'Reorder',
                TODAY: 'Today',
                YESTERDAY: 'Yesterday',
                TOTAL: 'Total',
                NO_ORDERS: 'No orders found',
            },
            PROFILE: {
                LOGOUT: 'Log Out',
                EDIT: 'Edit Profile',
                ORDERS: 'My Orders',
                ORDERS_SUBTITLE: 'View history and status',
                ADDRESSES: 'My Addresses',
                ADDRESSES_SUBTITLE: 'Manage delivery locations',
                PAYMENTS: 'Payment Methods',
                LANGUAGE: 'Language',
                LANGUAGE_SUBTITLE: 'Change the application language',
                DEFAULT_BADGE: 'Default',
                FAVORITES: 'My Favorites',
                FAVORITES_SUBTITLE: 'Your saved restaurants',
                SET_AS_DEFAULT: 'Set as default address',
                DELETE_ACCOUNT: 'Delete Account',
                DELETE_ACCOUNT_SUBTITLE: 'Permanently deactivate your account',
                DELETE_CONFIRM_TITLE: 'Are you sure?',
                DELETE_CONFIRM_BODY: 'Your account will be blocked and you won\'t be able to log in again. You will need to contact administration to recover it.',
                DELETE_MODAL_TITLE: 'Delete Account',
                DELETE_MODAL_DESC: 'We\'re sorry to see you go. Please tell us why you want to delete your account.',
                DELETE_REASON_LABEL: 'Reason for leaving',
                DELETE_REASON_PLACEHOLDER: 'Select a reason',
                DELETE_COMMENTS_LABEL: 'Additional comments (optional)',
                DELETE_COMMENTS_PLACEHOLDER: 'Tell us a bit more...',
                DELETE_CONFIRM_CHECKBOX: 'I understand that this action is permanent and I won\'t be able to recover my data.',
                DELETE_CONFIRM_BTN: 'Confirm deletion',
                REASON_EXPENSIVE: 'Too expensive',
                REASON_SERVICE: 'Bad service',
                REASON_PRIVACY: 'Privacy concerns',
                REASON_NOT_USEFUL: 'Not useful for me',
                REASON_OTHER: 'Other reason'
            },
            PRODUCT: {
                DETAIL: 'Product Detail',
                ADD_MORE: 'Add more',
                DESCRIPTION: 'Description',
                SOLD_OUT: 'Sold Out',
                ONLY_LEFT: 'Only left',
                STOCK_LIMIT: 'Stock limit reached'
            },
            AUTH: {
                LOGIN_TITLE: 'Log In',
                LOGIN_SUBTITLE: 'Welcome back to Pinsapo Market',
                SIGNUP_TITLE: 'Create Account',
                SIGNUP_SUBTITLE: 'Join the Pinsapo Market',
                NAME_LABEL: 'Full Name',
                NAME_PLACEHOLDER: 'Your name',
                NAME_REQUIRED: 'Name is required',
                EMAIL_LABEL: 'Email Address',
                EMAIL_PLACEHOLDER: 'example@email.com',
                EMAIL_REQUIRED: 'Email is required',
                EMAIL_INVALID: 'Invalid email',
                PASSWORD_LABEL: 'Password',
                PASSWORD_PLACEHOLDER: 'Your secure password',
                PASSWORD_REQUIRED: 'Password is required',
                PASSWORD_MINLENGTH: 'Minimum 6 characters',
                CONFIRM_PASSWORD_LABEL: 'Confirm Password',
                CONFIRM_PASSWORD_PLACEHOLDER: 'Repeat your password',
                CONFIRM_PASSWORD_REQUIRED: 'Confirm password is required',
                PASSWORDS_NOT_MATCH: 'Passwords do not match',
                PHONE_LABEL: 'Phone Number',
                PHONE_PLACEHOLDER: 'Your phone number',
                PHONE_REQUIRED: 'Phone is required',
                STREET_LABEL: 'Street and number',
                STREET_PLACEHOLDER: 'Ex: Main St, 123',
                STREET_REQUIRED: 'Street is required',
                CITY_LABEL: 'City',
                CITY_PLACEHOLDER: 'Your city',
                CITY_REQUIRED: 'City is required',
                ZIP_LABEL: 'ZIP Code',
                ZIP_PLACEHOLDER: '12345',
                ZIP_REQUIRED: 'ZIP code is required',
                BIRTHDATE_LABEL: 'Date of Birth',
                BIRTHDATE_REQUIRED: 'Date of birth is required',
                GENDER_LABEL: 'Gender',
                GENDER_REQUIRED: 'Gender is required',
                ACCEPTTERMS_REQUIRED: 'You must accept the policies to continue.',
                ACCEPT_TERMS_REQUIRED: 'You must accept the policies to continue.',
                SELECT_GENDER: 'Select your gender',
                GENDER_MALE: 'Male',
                GENDER_FEMALE: 'Female',
                GENDER_OTHER: 'Other',
                GENDER_PREFER_NOT: 'Prefer not to say',
                POLICIES_TEXT: 'I have read and accept the',
                POLICIES_LINK: 'Pinsapo Market policies',
                POLICIES_TITLE: 'Pinsapo Market Policies',
                POLICIES_UNDERSTOOD: 'Understood',
                LOGIN_BUTTON: 'Log In',
                SIGNUP_BUTTON: 'Sign Up',
                NO_ACCOUNT: 'Don\'t have an account?',
                SIGNUP_LINK: 'Sign up here',
                HAVE_ACCOUNT: 'Already have an account?',
                LOGIN_LINK: 'Log in here',
                CHECK_EMAIL: 'Account created! Please check your email.',
                EMAIL_NOT_CONFIRMED: 'Email not confirmed. Please check your inbox.',
                INVALID_CREDENTIALS: 'Invalid credentials.',
                FORGOT_PASSWORD_LINK: 'Forgot your password?',
                FORGOT_PASSWORD_TITLE: 'Recover Password',
                FORGOT_PASSWORD_SUBTITLE: 'Enter your email to receive a recovery link',
                SEND_RESET_LINK: 'Send link',
                BACK_TO_LOGIN: 'Back to login',
                RESET_PASSWORD_TITLE: 'New Password',
                RESET_PASSWORD_SUBTITLE: 'Enter your new password',
                UPDATE_PASSWORD_BUTTON: 'Update password',
                PASSWORD_RESET_SENT: 'Recovery email sent. Check your inbox.',
                PASSWORD_UPDATED: 'Password updated successfully.',
                SESSION_EXPIRED: 'The recovery session has expired or is invalid. Please request a new link.',
                POLICIES_SECTION_1_TITLE: '1. About Pinsapo Market',
                POLICIES_SECTION_1_DESC: 'Pinsapo Market is a technological platform that acts as a bridge between local businesses (Partners) and their customers. We provide digital tools so that each establishment can efficiently manage its products, reservations, and orders.',
                POLICIES_SECTION_2_TITLE: '2. Responsibility of Establishments',
                POLICIES_SECTION_2_DESC: 'Each Partner is solely responsible for the quality of its products, the preparation of its orders, and the fulfillment of its reservations. Pinsapo Market is not responsible for possible damages, shipping errors, or failures in the service provided by the establishments, which act independently.',
                POLICIES_SECTION_3_TITLE: '3. Shipping and Orders',
                POLICIES_SECTION_3_DESC: 'The legal responsibility for any logistical incident (delays, damages during transport, or incorrect orders) falls exclusively on the Partner who owns the requested service.',
                POLICIES_SECTION_4_TITLE: '4. Privacy and GDPR',
                POLICIES_SECTION_4_DESC: 'By creating an account, you agree that we process your personal data in accordance with our Privacy Policy and the General Data Protection Regulation (GDPR). Your data will only be used to manage your orders and offer you a personalized experience.',
                POLICIES_SECTION_5_TITLE: '5. Communications and Advertising',
                POLICIES_SECTION_5_DESC: 'You agree that Pinsapo Market may send you commercial communications, promotions, and news about establishments in your area. You can manage these preferences at any time from your profile.'
            },
            FAVORITES: {
                EMPTY_TITLE: 'No favorites yet',
                EMPTY_DESC: 'Save your favorite restaurants to order faster.',
                BROWSE: 'Browse Restaurants',
                REMOVE: 'Remove from favorites',
                RESTAURANTS: 'Restaurants',
                PRODUCTS: 'Products',
                GO_TO_RESTAURANT: 'Go to restaurant'
            },
            BOOKING: {
                MAX_GUESTS: 'Maximum guests',
                PRIVATE_SPACE: 'Unique and private space',
                SECURE_BOOKING: '100% secure booking',
                ABOUT_PROPERTY: 'About this property',
                CHOOSE_DATES: 'Choose your dates',
                DATES_UNAVAILABLE: 'Dates unavailable',
                NO_CHARGE_YET: 'You won\'t be charged yet',
                RULES_AND_SCHEDULE: 'Rules and Schedule',
                COMMENTS_PLACEHOLDER: 'Add special requests or additional comments...'
            }
        },
        fr: {
            COMMON: {
                OPEN: 'Ouvert',
                CANCEL: 'Annuler',
                CLOSED: 'Fermé',
                CATEGORIES: 'Catégories',
                SEARCH_PLACEHOLDER: 'Rechercher des produits...',
                VIEW_ALL: 'Voir tout',
                BACK: 'Retour',
                CART: 'PANIER',
                ORDERS: 'Commandes',
                PROFILE: 'Profil',
                HOME: 'Accueil',
                LOCATION: 'Localisation',
                SELECT_CITY: 'Choisir la Ville',
                ORDERING_FROM: 'Commande à',
                NO_RESULTS: 'Aucun résultat',
                ALL: 'Tous',
                FAVORITES: 'Favoris',
                MENU: 'Menu',
                SLOGAN: 'Votre marché local, livré chez vous',
                NO_CATEGORIES: 'Aucune catégorie',
                SEARCH_ADVANCED: 'Recherche',
                FREE_DELIVERY: 'Livraison gratuite',
                VACATION_RENTAL: 'Location de Vacances',
                NIGHT: 'nuit',
                NIGHTS: 'nuits',
                RESERVE_NOW: 'Réserver maintenant',
                AVAILABLE: 'Disponible',
                UNAVAILABLE: 'Non disponible',
                COMMENTS: 'Commentaires'
            },
            HOME: {
                LOCATION_PREFIX: 'Commande à',
                LOCATION_SUFFIX: 'et ses environs',
                FEATURED_STORES: 'Magasins vedettes',
                PROMO_TITLE: 'Achetez dans votre ville,',
                PROMO_SUBTITLE: 'Livraison à domicile !',
                NO_STORES: 'Aucun magasin disponible à',
                MAP_VIEW: 'Voir la carte'
            },
            CART: {
                CLEAR_TITLE: 'Vider le panier ?',
                CLEAR_CONFIRM: 'Êtes-vous sûr de vouloir supprimer tous les articles de votre panier ?',
                TITLE: 'Votre Commande',
                EMPTY_TITLE: 'Votre panier est vide',
                EMPTY_DESC: 'Trouvez tout ce dont vous avez besoin chez Pinsapo Market !',
                BROWSE: 'Voir Commerces',
                CLEAR: 'Effacer',
                SUMMARY: 'Résumé de la Commande',
                SUBTOTAL: 'Sous-total',
                DELIVERY_FEE: 'Frais de livraison',
                TOTAL: 'Total',
                PAY_CARD: 'Payer par Carte',
                PAY_BIZUM: 'Payer par Bizum',
                PAY_LOCAL: 'Payer sur Place',
                PAY_CASH: 'Payer en espèces',
                PAY_PHYSICAL_TPV: 'TPE physique',
                PHYSICAL_TPV_DESC: 'Payez par carte lorsque le livreur vous apporte votre commande',
                PAYMENT_METHOD: 'Mode de Paiement',
                CARD_DESC: 'Paiement sécurisé par carte',
                BIZUM_DESC: 'Paiement mobile instantané',
                LOCAL_DESC: 'Payez lorsque vous récupérez votre commande en magasin.',
                DISCOUNT_CODE: 'Code de Réduction',
                APPLY: 'Appliquer',
                ENTER_CODE: 'Entrez votre code',
                DISCOUNT: 'Réduction',
                DISCOUNT_APPLIED: 'Réduction appliquée',
                DISCOUNT_INVALID: 'Code invalide ou expiré',
                PROCESSING_ORDER: 'Traitement de la commande...',
                PROCESSING_TPV: 'Connexion au terminal TPV...',
                PROCESSING_BIZUM: 'Connexion à Bizum...',
                WAIT_TPV: 'Veuillez ne pas fermer cette fenêtre',
                WAIT_BIZUM: 'Confirmez le paiement dans votre application Bizum',
                SUCCESS_TITLE: 'Commande Confirmée !',
                SUCCESS_DESC: 'Votre commande a été traitée avec succès.',
                TOTAL_PAID: 'Total Payé',
                METHOD: 'Méthode',
                BACK_HOME: 'Retour à l\'accueil',
                GO_TO_ORDERS: 'Voir mes commandes',
                DELIVERY_ADDRESS: 'Adresse de Livraison',
                SELECT_ADDRESS: 'Choisir l\'adresse',
                ADD_ADDRESS: 'Ajouter une adresse',
                STREET: 'Rue et numéro',
                CITY: 'Ville',
                ZIP: 'Code Postal',
                LABEL: 'Étiquette (Maison, Travail...)',
                SAVE_ADDRESS: 'Enregistrer l\'adresse',
                CHANGE_ADDRESS: 'Modifier',
                UPDATE_ADDRESS: 'Mettre à jour l\'adresse',
                EDIT_ADDRESS: 'Modifier l\'adresse',
                CANCEL: 'Annuler',
                CITY_RESTRICTION: 'Vous ne pouvez pas ajouter de produits de différentes villes en même tiempo.',
                RESTAURANT_RESTRICTION: 'Vous ne pouvez pas ajouter de produits de différents restaurants à la même commande.',
                DELIVERY_AVAILABLE: 'Livraison disponible',
                PICKUP_ONLY: 'Retrait sur place uniquement'
            },
            RESTAURANT: {
                MENU: 'Carte',
                DELIVERY: 'Livraison',
                ADDED: 'Ajouté',
                RESERVE: 'Table réservée pour',
                PICK_DATE: 'Choisir une date',
                DINERS: 'Convives',
                TIME: 'Heure',
                CONTINUE_TO_MENU: 'Voir la carte et commander',
                TABLE_RESERVATION: 'Réservation de Table',
                RESERVATION_DATE: 'Date de Réservation',
                PLACE_ORDER: 'Passer la commande',
                BOOK_STAY: 'Réserver un séjour',
                CHECK_IN: 'Arrivée',
                CHECK_OUT: 'Départ',
                GUESTS: 'Voyageurs',
                SEE_AVAILABILITY: 'Voir Disponibilité',
                VIEW_MENU_NO_DATES: 'Voir options sans dates',
                VIEW_MENU_NO_RES: 'Voir la carte sans réservation',
                BOOK_APPOINTMENT: 'Prendre rendez-vous',
                BOOK_TABLE: 'Réserver une table',
                APPOINTMENT_DATE: 'Date du rendez-vous',
                APPOINTMENT_TIME: 'Heure du rendez-vous',
                CHECK_AVAILABILITY: 'Voir disponibilité',
                SEARCH_MENU: 'Rechercher dans le menu...',
                SEARCH_MENU_SHOP: 'Rechercher dans le menu...',
                SEARCH_MENU_RESERVATION: 'Rechercher dans les services...',
                RESERVE_ONLY: 'Réserver une table (Sans commande)'
            },
            ORDERS: {
                TITLE: 'Vos Commandes',
                ACTIVE: 'Commande en cours',
                PAST: 'Commandes Passées',
                STATUS_PENDING: 'En attente',
                STATUS_CONFIRMED: 'Confirmée',
                STATUS_PREPARING: 'En préparation',
                STATUS_DELIVERING: 'En route',
                STATUS_DELIVERED: 'Livré',
                STATUS_COMPLETED: 'Prêt',
                STATUS_CANCELLED: 'Annulée',
                STATUS_UNKNOWN: 'Statut inconnu',
                TRACK: 'Suivre la commande',
                REORDER: 'Commander à nouveau',
                TODAY: 'Aujourd\'hui',
                YESTERDAY: 'Hier',
                TOTAL: 'Total',
                NO_ORDERS: 'No orders found'
            },
            PROFILE: {
                LOGOUT: 'Déconnexion',
                EDIT: 'Modifier le profil',
                ORDERS: 'Mes commandes',
                ORDERS_SUBTITLE: 'Voir l\'historique et le statut',
                ADDRESSES: 'Mes adresses',
                ADDRESSES_SUBTITLE: 'Gérer les lieux de livraison',
                PAYMENTS: 'Modes de paiement',
                LANGUAGE: 'Langue',
                LANGUAGE_SUBTITLE: 'Changer la langue de l\'application',
                DEFAULT_BADGE: 'Par défaut',
                FAVORITES: 'Mes Favoris',
                FAVORITES_SUBTITLE: 'Vos restaurants enregistrés',
                SET_AS_DEFAULT: 'Définir comme adresse par défaut'
            },
            PRODUCT: {
                DETAIL: 'Détails du produit',
                ADD_MORE: 'Ajouter plus',
                DESCRIPTION: 'Description',
                SOLD_OUT: 'Épuisé',
                ONLY_LEFT: 'Seulement restant',
                STOCK_LIMIT: 'Limite de stock atteinte'
            },
            AUTH: {
                LOGIN_TITLE: 'Connexion',
                LOGIN_SUBTITLE: 'Bienvenue sur votre marché',
                SIGNUP_TITLE: 'Créer un Compte',
                SIGNUP_SUBTITLE: 'Rejoigne Pinsapo Market',
                NAME_LABEL: 'Nom Complet',
                NAME_PLACEHOLDER: 'Votre nom',
                NAME_REQUIRED: 'Le nom est requis',
                EMAIL_LABEL: 'Adresse E-mail',
                EMAIL_PLACEHOLDER: 'exemple@email.com',
                EMAIL_REQUIRED: 'L\'e-mail est requis',
                EMAIL_INVALID: 'E-mail non valide',
                PASSWORD_LABEL: 'Mot de passe',
                PASSWORD_PLACEHOLDER: 'Votre mot de passe sécurisé',
                PASSWORD_REQUIRED: 'Le mot de passe est requis',
                PASSWORD_MINLENGTH: 'Minimum 6 caractères',
                CONFIRM_PASSWORD_LABEL: 'Confirmer le mot de passe',
                CONFIRM_PASSWORD_PLACEHOLDER: 'Répétez votre mot de passe',
                CONFIRM_PASSWORD_REQUIRED: 'Confirmer le mot de passe est requis',
                PASSWORDS_NOT_MATCH: 'Les mots de passe ne correspondent pas',
                PHONE_LABEL: 'Téléphone',
                PHONE_PLACEHOLDER: 'Votre numéro de téléphone',
                PHONE_REQUIRED: 'Le téléphone est requis',
                STREET_LABEL: 'Rue et número',
                STREET_PLACEHOLDER: 'Ex: Rue Principale, 123',
                STREET_REQUIRED: 'La rue est requise',
                CITY_LABEL: 'Ville',
                CITY_PLACEHOLDER: 'Votre ville',
                CITY_REQUIRED: 'La ville est requise',
                ZIP_LABEL: 'Code Postal',
                ZIP_PLACEHOLDER: '12345',
                ZIP_REQUIRED: 'Le code postal est requis',
                LOGIN_BUTTON: 'Se Connecter',
                SIGNUP_BUTTON: 'S\'inscrire',
                NO_ACCOUNT: 'Pas encore de compte ?',
                SIGNUP_LINK: 'Inscrivez-vous ici',
                HAVE_ACCOUNT: 'Vous avez déjà un compte ?',
                LOGIN_LINK: 'Connectez-vous ici',
                CHECK_EMAIL: 'Compte créé ! Veuillez vérifier votre e-mail.',
                EMAIL_NOT_CONFIRMED: 'E-mail non confirmé. Veuillez vérifier votre boîte de réception.',
                INVALID_CREDENTIALS: 'Identifiants invalides.',
                FORGOT_PASSWORD_LINK: 'Mot de passe oublié ?',
                FORGOT_PASSWORD_TITLE: 'Récupérer le mot de passe',
                FORGOT_PASSWORD_SUBTITLE: 'Entrez votre e-mail pour recevoir un lien de récupération',
                SEND_RESET_LINK: 'Envoyer le lien',
                BACK_TO_LOGIN: 'Retour à la connexion',
                RESET_PASSWORD_TITLE: 'Nouveau mot de passe',
                RESET_PASSWORD_SUBTITLE: 'Entrez votre nouveau mot de passe',
                UPDATE_PASSWORD_BUTTON: 'Mettre à jour',
                PASSWORD_RESET_SENT: 'E-mail de récupération envoyé.',
                PASSWORD_UPDATED: 'Mot de passe mis à jour.',
                SESSION_EXPIRED: 'La session de récupération a expiré ou est invalide. Veuillez demander un nouveau lien.',
                POLICIES_SECTION_1_TITLE: '1. À propos de Pinsapo Market',
                POLICIES_SECTION_1_DESC: 'Pinsapo Market est une plateforme technologique qui sert de pont entre les entreprises locales (Partenaires) et leurs clients. Nous fournissons des outils numériques pour que chaque établissement puisse gérer efficacement ses produits, réservations et commandes.',
                POLICIES_SECTION_2_TITLE: '2. Responsabilité des Établissements',
                POLICIES_SECTION_2_DESC: 'Chaque Partenaire est seul responsable de la qualité de ses produits, de la préparation de ses commandes et de l\'exécution de ses réservations. Pinsapo Market n\'est pas responsable des éventuels dommages, erreurs d\'expédition ou défaillances dans le service fourni par les établissements, qui agissent de manière indépendante.',
                POLICIES_SECTION_3_TITLE: '3. Expéditions et Commandes',
                POLICIES_SECTION_3_DESC: 'La responsabilité légale de tout incident logistique (retards, dommages pendant le transport ou commandes incorrectes) incombe exclusivement au Partenaire propriétaire du service demandé.',
                POLICIES_SECTION_4_TITLE: '4. Confidentialité et RGPD',
                POLICIES_SECTION_4_DESC: 'En créant un compte, vous acceptez que nous traitions vos données personnelles conformément à notre Politique de confidentialité et au Règlement général sur la protection des données (RGPD). Vos données ne seront utilisées que pour gérer vos commandes et vous offrir une expérience personnalisée.',
                POLICIES_SECTION_5_TITLE: '5. Communications et Publicité',
                POLICIES_SECTION_5_DESC: 'Vous acceptez que Pinsapo Market puisse vous envoyer des communications commerciales, des promotions et des nouvelles concernant les établissements de votre région. Vous pouvez gérer ces préférences à tout moment depuis votre profil.'
            },
            FAVORITES: {
                EMPTY_TITLE: 'Pas encore de favoris',
                EMPTY_DESC: 'Enregistrez vos restaurants préférés pour commander plus rapidement.',
                BROWSE: 'Explorer les Restaurants',
                REMOVE: 'Retirer des favoris',
                RESTAURANTS: 'Restaurants',
                PRODUCTS: 'Produits',
                GO_TO_RESTAURANT: 'Aller au restaurant'
            },
            BOOKING: {
                MAX_GUESTS: 'Nombre maximum de voyageurs',
                PRIVATE_SPACE: 'Espace unique et privé',
                SECURE_BOOKING: 'Réservation 100% sécurisée',
                ABOUT_PROPERTY: 'À propos de cette propriété',
                CHOOSE_DATES: 'Choisissez vos dates',
                DATES_UNAVAILABLE: 'Dates non disponibles',
                NO_CHARGE_YET: 'Rien ne vous será facturé pour le moment',
                RULES_AND_SCHEDULE: 'Règles et Horaires',
                COMMENTS_PLACEHOLDER: 'Ajouter des demandes spéciales ou des commentaires supplémentaires...'
            }
        },
        zh: {
            COMMON: {
                OPEN: '开业',
                CANCEL: '取消',
                CLOSED: '关闭',
                CATEGORIES: '分类',
                SEARCH_PLACEHOLDER: '搜索商品或店铺...',
                VIEW_ALL: '查看全部',
                BACK: '返回',
                CART: '购物车',
                ORDERS: '订单',
                PROFILE: '个人资料',
                HOME: '首页',
                LOCATION: '位置',
                SELECT_CITY: '选择城市',
                ORDERING_FROM: '正在订购自',
                NO_RESULTS: '暂无结果',
                ALL: '全部',
                FAVORITES: '收藏夹',
                MENU: '菜单',
                LOADING: '加载中',
                SLOGAN: '您的本地市场，直达家门',
                NO_CATEGORIES: '暂无分类',
                SEARCH_ADVANCED: '高级搜索',
                FREE_DELIVERY: '免费配送',
                VACATION_RENTAL: '度假短期租赁',
                NIGHT: '晚',
                NIGHTS: '晚',
                RESERVE_NOW: '立即预订',
                AVAILABLE: '可用',
                UNAVAILABLE: '不可用',
                COMMENTS: '备注'
            },
            HOME: {
                LOCATION_PREFIX: '下单于',
                LOCATION_SUFFIX: '及周边地区',
                FEATURED_STORES: '推荐店铺',
                PROMO_TITLE: '支持家乡好貨，',
                PROMO_SUBTITLE: '配送到家！',
                NO_STORES: '此地区暂无可用店铺',
                MAP_VIEW: '地图预览'
            },
            CART: {
                CLEAR_TITLE: '清空购物车？',
                CLEAR_CONFIRM: '您确定要从购物车中删除所有商品吗？',
                TITLE: '您的订单',
                EMPTY_TITLE: '购物车是空的',
                EMPTY_DESC: '去发现 Pinsapo Market 您需要的商品！',
                BROWSE: '浏览店铺',
                CLEAR: '清空',
                SUMMARY: '订单摘要',
                SUBTOTAL: '小计',
                DELIVERY_FEE: '配送费',
                TOTAL: '总计',
                PAY_CARD: '银行卡支付',
                PAY_BIZUM: '使用 Bizum 支付',
                PAY_LOCAL: '到店支付',
                PAY_CASH: '现金支付',
                PAY_PHYSICAL_TPV: '实体 POS 机',
                PHYSICAL_TPV_DESC: '送货员到达时刷卡支付',
                PAYMENT_METHOD: '支付方式',
                CARD_DESC: '安全的卡支付',
                BIZUM_DESC: '快速手机支付',
                LOCAL_DESC: '到店取货时支付',
                DISCOUNT_CODE: '折扣码',
                APPLY: '应用',
                ENTER_CODE: '输入折扣码',
                DISCOUNT: '折扣',
                DISCOUNT_APPLIED: '折扣已应用',
                DISCOUNT_INVALID: '折扣码无效或已过期',
                PROCESSING_ORDER: '正在处理订单...',
                PROCESSING_TPV: '正在连接支付终端...',
                PROCESSING_BIZUM: '正在连接 Bizum...',
                WAIT_TPV: '请勿关闭此窗口',
                WAIT_BIZUM: '请在您的 Bizum 应用中确认支付',
                SUCCESS_TITLE: '订单已确认！',
                SUCCESS_DESC: '您的订单已成功处理。',
                TOTAL_PAID: '实付总额',
                METHOD: '方式',
                BACK_HOME: '返回首页',
                GO_TO_ORDERS: '查看我的订单',
                DELIVERY_ADDRESS: '收货地址',
                SELECT_ADDRESS: '选择地址',
                ADD_ADDRESS: '添加新地址',
                STREET: '街道及门牌号',
                CITY: '城市',
                ZIP: '邮政编码',
                LABEL: '标签（家、公司...）',
                SAVE_ADDRESS: '保存地址',
                CHANGE_ADDRESS: '更改',
                UPDATE_ADDRESS: '更新地址',
                EDIT_ADDRESS: '编辑地址',
                CANCEL: '取消',
                CITY_RESTRICTION: '您不能同时添加来自不同城市的商品。',
                RESTAURANT_RESTRICTION: '您不能在同一个订单中添加来自不同餐厅的商品。',
                DELIVERY_AVAILABLE: '支持配送',
                PICKUP_ONLY: '仅限自提'
            },
            RESTAURANT: {
                MENU: '菜单',
                DELIVERY: '配送',
                ADDED: '已添加',
                RESERVE: '桌位已预订',
                PICK_DATE: '选择日期',
                DINERS: '人数',
                TIME: '时间',
                CONTINUE_TO_MENU: '查看菜单并下单',
                TABLE_RESERVATION: '预订桌位',
                RESERVATION_DATE: '预订日期',
                PLACE_ORDER: '下单',
                BOOK_STAY: '预订住宿',
                CHECK_IN: '入住',
                CHECK_OUT: '退房',
                GUESTS: '访客',
                SEE_AVAILABILITY: '查看房态',
                VIEW_MENU_NO_DATES: '无需日期查看选项',
                VIEW_MENU_NO_RES: '无需预订查看菜单',
                BOOK_APPOINTMENT: '预约',
                BOOK_TABLE: '预订桌位',
                APPOINTMENT_DATE: '预约日期',
                APPOINTMENT_TIME: '预约时间',
                CHECK_AVAILABILITY: '查看时段',
                SEARCH_MENU: '搜索菜单...',
                SEARCH_MENU_SHOP: '搜索商品...',
                SEARCH_MENU_RESERVATION: '搜索服务...',
                RESERVE_ONLY: '仅订座（不下单）',
                CHOOSE_OPTION: '您想要做什么？'
            },
            ORDERS: {
                TITLE: '我的订单',
                ACTIVE: '进行中的订单',
                PAST: '历史订单',
                STATUS_PENDING: '待处理',
                STATUS_CONFIRMED: '已确认',
                STATUS_PREPARING: '准备中',
                STATUS_DELIVERING: '配送中',
                STATUS_DELIVERED: '已送达',
                STATUS_COMPLETED: '已就绪',
                STATUS_CANCELLED: '已取消',
                STATUS_UNKNOWN: '状态未知',
                TRACK: '追踪订单',
                REORDER: '再次下单',
                TODAY: '今天',
                YESTERDAY: '昨天',
                TOTAL: '总计',
                NO_ORDERS: 'No orders found'
            },
            PROFILE: {
                LOGOUT: '退出登录',
                EDIT: '编辑个人资料',
                ORDERS: '我的订单',
                ORDERS_SUBTITLE: '查看历史记录和状态',
                ADDRESSES: '我的地址',
                ADDRESSES_SUBTITLE: '管理配送地址',
                PAYMENTS: '支付方式',
                LANGUAGE: '语言',
                LANGUAGE_SUBTITLE: '更改应用语言',
                DEFAULT_BADGE: '默认',
                FAVORITES: '收藏夹',
                FAVORITES_SUBTITLE: '您收藏的店铺',
                SET_AS_DEFAULT: '设为默认地址'
            },
            PRODUCT: {
                DETAIL: '商品详情',
                ADD_MORE: '添加更多',
                DESCRIPTION: '描述',
                SOLD_OUT: '已售罄',
                ONLY_LEFT: '仅剩',
                STOCK_LIMIT: '库存限制'
            },
            AUTH: {
                LOGIN_TITLE: '登录',
                LOGIN_SUBTITLE: '欢迎回到您的市场',
                SIGNUP_TITLE: '创建账户',
                SIGNUP_SUBTITLE: '加入 Pinsapo 社区',
                NAME_LABEL: '全名',
                NAME_PLACEHOLDER: '您的姓名',
                NAME_REQUIRED: '姓名是必填项',
                EMAIL_LABEL: '电子邮箱',
                EMAIL_PLACEHOLDER: 'example@email.com',
                EMAIL_REQUIRED: '邮箱是必填项',
                EMAIL_INVALID: '邮箱格式不正确',
                PASSWORD_LABEL: '密码',
                PASSWORD_PLACEHOLDER: '设置安全密码',
                PASSWORD_REQUIRED: '密码是必填项',
                PASSWORD_MINLENGTH: '最少 6 个字符',
                CONFIRM_PASSWORD_LABEL: '确认密码',
                CONFIRM_PASSWORD_PLACEHOLDER: '重复您的密码',
                CONFIRM_PASSWORD_REQUIRED: '需要确认密码',
                PASSWORDS_NOT_MATCH: '密码不匹配',
                PHONE_LABEL: '电话号码',
                PHONE_PLACEHOLDER: '您的电话号码',
                PHONE_REQUIRED: '电话号码是必填项',
                STREET_LABEL: '街道及门牌号',
                STREET_PLACEHOLDER: '例如：主街 123 号',
                STREET_REQUIRED: '街道是必填项',
                CITY_LABEL: '城市',
                CITY_PLACEHOLDER: '您的城市',
                CITY_REQUIRED: '城市是必填项',
                ZIP_LABEL: '邮政编码',
                ZIP_PLACEHOLDER: '12345',
                ZIP_REQUIRED: '邮政编码是必填项',
                LOGIN_BUTTON: '登录',
                SIGNUP_BUTTON: '注册',
                NO_ACCOUNT: '还没有账户？',
                SIGNUP_LINK: '点击这里注册',
                HAVE_ACCOUNT: '已有账户？',
                LOGIN_LINK: '点击这里登录',
                CHECK_EMAIL: '账户已创建！请查看邮件以进行确认。',
                EMAIL_NOT_CONFIRMED: '邮箱未确认。请查看收件箱。',
                INVALID_CREDENTIALS: '凭据无效。',
                FORGOT_PASSWORD_LINK: '忘记密码？',
                FORGOT_PASSWORD_TITLE: '找回密码',
                FORGOT_PASSWORD_SUBTITLE: '输入您的电子邮件以接收重置链接',
                SEND_RESET_LINK: '发送链接',
                BACK_TO_LOGIN: '返回登录',
                RESET_PASSWORD_TITLE: '新密码',
                RESET_PASSWORD_SUBTITLE: '输入您的新密码',
                UPDATE_PASSWORD_BUTTON: '更新密码',
                PASSWORD_RESET_SENT: '重置邮件已发送。',
                PASSWORD_UPDATED: '密码更新成功。',
                SESSION_EXPIRED: '恢复会话已过期或无效。请重新请求链接。',
                POLICIES_SECTION_1_TITLE: '1. 关于 Pinsapo Market',
                POLICIES_SECTION_1_DESC: 'Pinsapo Market 是一个技术平台，作为本地商家（合作伙伴）与其客户之间的桥梁。我们提供数字化工具，以便每个机构都能高效管理其产品、预订和订单。',
                POLICIES_SECTION_2_TITLE: '2. 机构的责任',
                POLICIES_SECTION_2_DESC: '每个合作伙伴对其产品质量、订单准备及预订履行承担全部责任。Pinsapo Market 不对独立运营的机构可能造成的损坏、运输错误或服务故障负责。',
                POLICIES_SECTION_3_TITLE: '3. 运输与订单',
                POLICIES_SECTION_3_DESC: '任何物流事件（延误、运输过程中的损坏或订单错误）的法律责任均由所请求服务的所有者合作伙伴独家承担。',
                POLICIES_SECTION_4_TITLE: '4. 隐私与 GDPR',
                POLICIES_SECTION_4_DESC: '创建账户即表示您同意我们根据我们的隐私政策和通用数据保护条例 (GDPR) 处理您的个人数据。您的数据将仅用于管理您的订单并为您提供个性化体验。',
                POLICIES_SECTION_5_TITLE: '5. 通信与广告',
                POLICIES_SECTION_5_DESC: '您同意 Pinsapo Market 可能会向您发送关于您所在地区机构的商业通信、促销活动和新闻。您可以随时在您的个人资料中管理这些首选项。'
            },
            FAVORITES: {
                EMPTY_TITLE: '暂无可用的收藏',
                EMPTY_DESC: '保存您喜欢的餐厅以便更快下单。',
                BROWSE: '浏览餐厅',
                REMOVE: '从收藏中移除',
                RESTAURANTS: '餐厅',
                PRODUCTS: '产品',
                GO_TO_RESTAURANT: '前往餐厅'
            },
            BOOKING: {
                MAX_GUESTS: '最多房客人数',
                PRIVATE_SPACE: '独特且私密的空间',
                SECURE_BOOKING: '100% 安全预订',
                ABOUT_PROPERTY: '关于此房源',
                CHOOSE_DATES: '选择日期',
                DATES_UNAVAILABLE: '日期不可选',
                NO_CHARGE_YET: '目前不会向您收取任何费用',
                RULES_AND_SCHEDULE: '规则与时间安排',
                COMMENTS_PLACEHOLDER: '添加特殊要求或额外备注...'
            }
        }
    };

    translate(key: string): string {
        const val = this.getStaticTranslation(key);
        return val || key;
    }

    getStaticTranslation(key: string): string | null {
        if (!key) return null;
        const keys = key.split('.');
        let result = this.translations[this.currentLang()];

        for (const k of keys) {
            if (result && result[k]) {
                result = result[k];
            } else {
                return null;
            }
        }
        return typeof result === 'string' ? result : null;
    }
}
